/**
 * Makes the assistant's hidden frame read-only.
 *
 * The assistant loads the user's own pages, logged in as them, in a hidden
 * frame to read them (components/assistant/assistant-view-protocol.ts). Loading
 * a page must never change anything, so in that frame every request that is not
 * a GET, HEAD or OPTIONS is refused before it leaves the browser: fetch, XHR and
 * sendBeacon alike. embed-write-guard-install.ts applies it, imported first by
 * api/fetch.ts so no later code can capture the unguarded functions.
 */
import { VIEW_TOKEN_PARAM } from "../components/assistant/assistant-view-protocol";

const READ_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/** Status of the stand-in response a refused fetch resolves with. */
export const REFUSED_STATUS = 405;

/** The browser objects the guard replaces; plain objects stand in for them in tests. */
export interface GuardTarget {
	fetch: typeof fetch;
	XMLHttpRequest?: typeof XMLHttpRequest;
	navigator?: Pick<Navigator, "sendBeacon">;
}

/** True in the frame the assistant opened, which marks its URL with a token. */
export const isAssistantViewFrame = (search: string): boolean =>
	new URLSearchParams(search).has(VIEW_TOKEN_PARAM);

const requestMethod = (input: RequestInfo | URL, init?: RequestInit): string => {
	const method = init?.method ?? (input instanceof Request ? input.method : "GET");
	return method.toUpperCase();
};

export const isReadMethod = (method: string): boolean =>
	READ_METHODS.has(method.toUpperCase());

const refusedResponse = (method: string): Response =>
	new Response(`${method} is refused in the assistant's read-only view`, {
		status: REFUSED_STATUS,
	});

/** Replace `target`'s write paths with ones that refuse. */
export const installEmbedWriteGuard = (target: GuardTarget): void => {
	const original = target.fetch;
	const guardedFetch = (input: RequestInfo | URL, init?: RequestInit) => {
		const method = requestMethod(input, init);
		if (isReadMethod(method)) return original.call(target, input, init);
		const url = input instanceof Request ? input.url : String(input);
		console.warn(`[Assistant view] refused ${method} ${url}`);
		return Promise.resolve(refusedResponse(method));
	};
	target.fetch = Object.assign(guardedFetch, original);

	const xhr = target.XMLHttpRequest?.prototype;
	if (xhr) {
		const open = xhr.open;
		xhr.open = function (this: XMLHttpRequest, method: string, ...rest: [string]) {
			if (!isReadMethod(method)) {
				throw new Error(
					`${method} is refused in the assistant's read-only view`
				);
			}
			return open.call(this, method, ...rest);
		} as typeof xhr.open;
	}

	const navigator = target.navigator;
	if (navigator?.sendBeacon) {
		// A beacon is always a POST.
		navigator.sendBeacon = () => false;
	}
};
